import { useState, useEffect, useCallback } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@retailsync/ui'
import { useAuth } from '../contexts/AuthContext'
import { getImportHistory, clearImportHistory, type ImportHistoryEntry } from '../services/connector-engine'
import { connectorApi, type UploadStatus } from '../services/api/connector'

/**
 * Import history — local engine runs and backend API uploads.
 *
 * This page previously rendered TWO identical tab bars (one labelled
 * "Local Imports", a copy-paste duplicate labelled "Local Engines"), so the user
 * saw four buttons for two tabs. It also hand-rolled its own tables, buttons and
 * headings instead of using the shared design system, which is why it looked
 * unlike the rest of the product and did not reflow on mobile.
 *
 * Both problems are fixed: a single Tabs group, and shared Table/Button/Badge so
 * the page matches the design system and scrolls horizontally on small screens.
 */
export function ImportHistory() {
  const { tenant } = useAuth()
  const [localHistory, setLocalHistory] = useState<ImportHistoryEntry[]>([])
  const [apiUploads, setApiUploads] = useState<UploadStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'engine' | 'api'>('engine')

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Local engine history (sessionStorage)
      if (tenant?.id) {
        setLocalHistory(getImportHistory(tenant.id))
      }

      // API uploads (from backend)
      try {
        setApiUploads(await connectorApi.getUploads())
      } catch {
        // API might not be available — that's fine for local mode
        setApiUploads([])
      }
    } catch (err) {
      console.error('Failed to fetch history:', err)
      setError('Failed to load import history')
    } finally {
      setLoading(false)
    }
  }, [tenant?.id])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const handleClearLocal = () => {
    if (tenant?.id) {
      clearImportHistory(tenant.id)
      setLocalHistory([])
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header — stacks on mobile so the actions never squeeze the title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Import History</h1>
          <p className="mt-1 text-sm text-gray-600">View and manage your data import history</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadHistory} loading={loading}>
            Refresh
          </Button>
          {localHistory.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleClearLocal} className="text-red-600">
              Clear Local
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-600">{error}</CardContent>
        </Card>
      )}

      {/* ONE tab group. This used to be duplicated, showing four buttons for two tabs. */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'engine' | 'api')}>
        <TabsList>
          <TabsTrigger value="engine">Local Imports ({localHistory.length})</TabsTrigger>
          <TabsTrigger value="api">API Uploads ({apiUploads.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Engine History Tab */}
      {activeTab === 'engine' && (
        <>
          {localHistory.length === 0 ? (
            <EmptyState
              title="No local imports yet."
              hint="Run an import through the Import Wizard to see results here."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Processed</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {localHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap text-gray-600">
                      {formatDate(entry.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="info">{entry.entity}</Badge>
                    </TableCell>
                    <TableCell className="text-gray-700">{entry.provider}</TableCell>
                    <TableCell className="text-right font-medium">{entry.totalRecords}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {entry.processedRecords}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        entry.failedRecords > 0 ? 'text-red-600' : 'text-gray-400'
                      }`}
                    >
                      {entry.failedRecords}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-gray-500">
                      {(entry.duration / 1000).toFixed(2)}s
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {/* API Uploads Tab */}
      {activeTab === 'api' && (
        <>
          {apiUploads.length === 0 ? (
            <EmptyState
              title="No API uploads found."
              hint="Backend uploads will appear here when the API is connected."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Processed</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiUploads.map((upload) => (
                  <TableRow key={upload.id}>
                    <TableCell className="font-medium text-gray-900">{upload.filename}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          upload.status === 'completed'
                            ? 'success'
                            : upload.status === 'failed'
                            ? 'destructive'
                            : upload.status === 'processing'
                            ? 'info'
                            : 'default'
                        }
                      >
                        {upload.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {upload.recordsImported}
                    </TableCell>
                    <TableCell className="text-right text-red-600">{upload.recordsFailed}</TableCell>
                    <TableCell className="whitespace-nowrap text-gray-500">
                      {formatDate(upload.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  )
}

function EmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 text-center">
        <p className="text-gray-500">{title}</p>
        <p className="mt-1 text-sm text-gray-400">{hint}</p>
      </CardContent>
    </Card>
  )
}
